import type { PromptConfig } from '../../../shared/types';

export interface PromptProfile {
  id: string;
  name: string;
  description: string;
  promptConfig: PromptConfig;
}

export const PROMPT_PROFILE_STORAGE_KEY = 'novel-outline-prompt-profiles-v1';
export const ACTIVE_PROMPT_PROFILE_STORAGE_KEY = 'novel-outline-active-prompt-profile-v1';

export const DEFAULT_PROMPT_PROFILES: PromptProfile[] = [
  {
    id: 'default-balanced',
    name: '默认策划版',
    description: '适合大多数长篇项目，强调主线递进、卷纲可执行和章纲具体事件。',
    promptConfig: {
      ruleObservationExtra: '拆书观察时必须优先抽取每章的目标、阻力、结果、动作链、关系变化和章末悬念，再总结题材与节奏，不要只做题材标签归纳。',
      ruleSynthesisExtra: '规则提炼必须把“目标->阻力->动作->结果->悬念”压成可复用写作公式，并明确阶段推进链、资源规则和读者反馈点。',
      standardOutlineExtra: '标准大纲必须写清主线目标、阶段转折、升级资源和终局代价，不能停留在泛泛简介。',
      goldenChapterExtra: '示范正文要尽快进入冲突现场，优先展示人物目标、当前阻力和章末钩子，避免空泛铺垫。',
      threeTierTotalExtra: '总纲必须写清每个阶段的外部对手、关键资源、升级门槛和翻盘代价，不能只有成长概述。',
      threeTierVolumeExtra: '每卷都要有阶段目标、核心事件、资源争夺、关键爆点和卷尾钩子，避免重复句式和模板化概括。',
      threeTierChapterExtra: '每章都要写出具体事件、冲突升级、角色动作和章末钩子，不能只写本章成长或情绪变化。',
    },
  },
  {
    id: 'strong-conflict',
    name: '强冲突版',
    description: '强调对抗、升级、反转和资源争夺，适合爽文、连载型项目。',
    promptConfig: {
      ruleObservationExtra: '拆书观察时重点追踪每章冲突发起者、阻力升级方式、动作反制和结果反馈，优先识别连续的冲突链与悬念链。',
      ruleSynthesisExtra: '规则提炼要明确“目标受阻 -> 动作反击 -> 结果兑现 -> 新风险挂钩”的连载公式，并指出最常见的翻盘方式。',
      standardOutlineExtra: '每章都要体现主角推进、外部压力和可见收益，避免平铺直叙。',
      goldenChapterExtra: '正文要尽快进入冲突现场，减少说明文字，优先制造悬念和短期收益。',
      threeTierTotalExtra: '全书规划要明确每一阶段的主要对手、关键资源和升级门槛，不能写成泛泛成长线。',
      threeTierVolumeExtra: '每卷都要体现阶段目标、外部阻力、资源争夺和卷末翻盘/反噬，避免重复同一种推进句式。',
      threeTierChapterExtra: '每章都必须有明确动作、冲突推进和章末钩子，尽量把事件写成场景，而不是概括。',
    },
  },
  {
    id: 'workplace-competition',
    name: '职场资源战版',
    description: '适合都市、职场、商战类项目，强调职位晋升、资源争夺、权力关系和利益代价。',
    promptConfig: {
      ruleObservationExtra: '拆书观察时重点记录项目目标、上级压力、资源争夺、会议/汇报/背锅/翻盘动作以及关系站队变化，不要把职场内容抽象成泛成长。',
      ruleSynthesisExtra: '规则提炼要把职位目标、项目战场、资源争夺对象、内部关系和失败代价压成可复用的职场连载公式。',
      standardOutlineExtra: '大纲要突出职位变化、绩效压力、资源争夺和权力关系，不能写成泛泛鸡汤成长文。',
      goldenChapterExtra: '正文要尽快进入会议、汇报、抢项目、背锅、翻盘等高压职场场景，少讲道理，多给动作和博弈。',
      threeTierTotalExtra: '总纲必须明确每个阶段的职位目标、关键项目、竞争对手、内部关系和失败代价。',
      threeTierVolumeExtra: '每卷都要写出具体项目战场、资源争夺对象、上级压力、团队分歧和卷尾反转。',
      threeTierChapterExtra: '每章都要写清这章具体做什么事、遇到什么阻力、如何推进项目或争夺资源，以及章末留下什么风险。',
    },
  },
];

function cloneProfile(profile: PromptProfile): PromptProfile {
  return {
    id: profile.id,
    name: profile.name,
    description: profile.description,
    promptConfig: { ...profile.promptConfig },
  };
}

function normalizeProfile(profile: PromptProfile, index: number): PromptProfile {
  return {
    id: profile.id || `profile-${index + 1}`,
    name: profile.name || `提示词方案 ${index + 1}`,
    description: profile.description || '',
    promptConfig: profile.promptConfig || {},
  };
}

function mergeWithDefaultProfiles(profiles: PromptProfile[]): PromptProfile[] {
  const normalizedProfiles = profiles.map(normalizeProfile);
  const existingIds = new Set(normalizedProfiles.map((profile) => profile.id));
  const merged = [...normalizedProfiles];

  DEFAULT_PROMPT_PROFILES.forEach((profile) => {
    if (!existingIds.has(profile.id)) {
      merged.push(cloneProfile(profile));
    }
  });

  return merged;
}

export function loadPromptProfiles(): PromptProfile[] {
  const raw = localStorage.getItem(PROMPT_PROFILE_STORAGE_KEY);
  if (!raw) {
    return DEFAULT_PROMPT_PROFILES.map(cloneProfile);
  }

  try {
    const parsed = JSON.parse(raw) as PromptProfile[];
    if (!Array.isArray(parsed) || parsed.length === 0) {
      return DEFAULT_PROMPT_PROFILES.map(cloneProfile);
    }

    return mergeWithDefaultProfiles(parsed);
  } catch {
    return DEFAULT_PROMPT_PROFILES.map(cloneProfile);
  }
}

export function savePromptProfiles(profiles: PromptProfile[]): void {
  localStorage.setItem(PROMPT_PROFILE_STORAGE_KEY, JSON.stringify(profiles));
}

export function loadActivePromptProfileId(profiles: PromptProfile[]): string {
  const stored = localStorage.getItem(ACTIVE_PROMPT_PROFILE_STORAGE_KEY);
  if (stored && profiles.some((profile) => profile.id === stored)) {
    return stored;
  }

  return profiles[0]?.id || DEFAULT_PROMPT_PROFILES[0].id;
}

export function saveActivePromptProfileId(profileId: string): void {
  localStorage.setItem(ACTIVE_PROMPT_PROFILE_STORAGE_KEY, profileId);
}

export function getDefaultPromptProfileById(profileId: string): PromptProfile | null {
  const profile = DEFAULT_PROMPT_PROFILES.find((item) => item.id === profileId);
  return profile ? cloneProfile(profile) : null;
}
